import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios, Context } from "../../../utils/config";
import { OrderItemsDetail } from "./order-items-detail";

const dateFieldSx = {
  minWidth: 160,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: "6px",
  },
};

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("M/D/YY h:mm A") : "—";
}

function textCell(value) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {value || "—"}
    </Typography>
  );
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (field === "order_created_at") {
    return dayjs(va).valueOf() - dayjs(vb).valueOf();
  }
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function mapOrders(result) {
  const orders = result?.data?.orders;
  if (!Array.isArray(orders)) return [];

  return orders.map((order) => {
    const items = order.order_items || [];
    return {
      order_id: order.order_id,
      order_number: order.order_number,
      order_created_at: order.order_created_at,
      order_location_id: order.order_location_id,
      vendor_name: order.vendor_name,
      location_name: order.location_name,
      order_user_name: order.order_user_name,
      total_items: items.length,
      total_quantity: items.reduce(
        (sum, item) => sum + (item.order_item_product_amount || 0),
        0
      ),
      total_cost: items.reduce(
        (sum, item) =>
          sum +
          (item.order_item_product_amount || 0) *
            (item.product?.product_cost || 0),
        0
      ),
      order_items: items,
    };
  });
}

function mapApiError(error) {
  if (error.response?.status === 403) {
    return "Not allowed to see inventory orders data";
  }
  if (error.response?.status === 404) {
    return "Inventory orders endpoint not found";
  }
  return error.response?.data?.message || "Error loading inventory orders data";
}

/**
 * Shared Inventory Receiving report (standard + Mica variants)
 */
export function InventoryReceivingReport({
  title = "Inventory Receiving",
  subtitle = "Review received inventory orders and line-item details.",
  endpoint = "/inventory/orders",
  exportFileName = "InventoryReceiving",
  worksheetName = "Inventory Receiving",
}) {
  const { location: globalLocation } = useContext(Context);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [sortBy, setSortBy] = useState("order_created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setHasRun(true);
    try {
      const result = await baseAxios.get(endpoint);
      setRows(mapOrders(result));
    } catch (error) {
      setRows([]);
      toast.error(mapApiError(error), { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    loadReport();
  }, [loadReport, globalLocation?.location_id]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const created = dayjs(row.order_created_at);
      if (!created.isValid()) return true;
      if (startDate && dayjs(startDate).isValid()) {
        if (created.isBefore(dayjs(startDate).startOf("day"))) return false;
      }
      if (endDate && dayjs(endDate).isValid()) {
        if (created.isAfter(dayjs(endDate).endOf("day"))) return false;
      }
      return true;
    });
  }, [rows, startDate, endDate]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(worksheetName);
      worksheet.columns = [
        { header: "Order Number", key: "order_number", width: 16 },
        { header: "Vendor Name", key: "vendor_name", width: 22 },
        { header: "Order Date", key: "order_created_at", width: 18 },
        { header: "Location", key: "location_name", width: 18 },
        { header: "Ordered By", key: "order_user_name", width: 18 },
        { header: "Total Items", key: "total_items", width: 12 },
        { header: "Total Quantity", key: "total_quantity", width: 14 },
        { header: "Total Cost", key: "total_cost", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          ...row,
          order_created_at: formatDateTime(row.order_created_at),
          total_cost: Number(row.total_cost) || 0,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${exportFileName}_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [exportFileName, sortedRows, worksheetName]);

  const columns = useMemo(
    () => [
      {
        id: "order_number",
        label: "Order Number",
        field: "order_number",
        width: "12%",
        render: (v) => textCell(v),
      },
      {
        id: "vendor_name",
        label: "Vendor Name",
        field: "vendor_name",
        width: "16%",
        render: (v) => textCell(v),
      },
      {
        id: "order_created_at",
        label: "Order Date",
        field: "order_created_at",
        width: "14%",
        render: (v) => textCell(formatDateTime(v)),
      },
      {
        id: "location_name",
        label: "Location",
        field: "location_name",
        width: "12%",
        render: (v) => textCell(v),
      },
      {
        id: "order_user_name",
        label: "Ordered By",
        field: "order_user_name",
        width: "12%",
        render: (v) => textCell(v),
      },
      {
        id: "total_items",
        label: "Total Items",
        field: "total_items",
        width: "9%",
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "total_quantity",
        label: "Total Quantity",
        field: "total_quantity",
        width: "10%",
        align: "center",
        render: (v) => Number(v) || 0,
      },
      {
        id: "total_cost",
        label: "Total Cost",
        field: "total_cost",
        width: "10%",
        align: "right",
        render: (v) => formatMoney(v),
      },
    ],
    []
  );

  const renderDetail = useCallback(
    (row) => <OrderItemsDetail data={row} />,
    []
  );

  const searchKey = `${endpoint}|${startDate ? dayjs(startDate).format("YYYY-MM-DD") : "all"}|${endDate ? dayjs(endDate).format("YYYY-MM-DD") : "all"}|${globalLocation?.location_id ?? "all"}|${rows.length}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: dateFieldSx,
                },
              }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: "small",
                  sx: dateFieldSx,
                },
              }}
            />
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "6px",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        <PaginatedTable
          columns={columns}
          rows={sortedRows}
          totalRowCount={sortedRows.length}
          loading={loading}
          onPaginationChange={noopPagination}
          onSortChange={handleSortChange}
          initialOrderBy="order_created_at"
          initialOrder="desc"
          searchQuery={searchKey}
          clientSidePagination
          skeletonHeight="40px"
          getRowKey={(row) => row.order_id}
          renderDetail={renderDetail}
          fitWidth
          emptyMessage={
            hasRun
              ? "No inventory orders found"
              : "Click Run Report to load orders"
          }
        />
      </Box>
    </LocalizationProvider>
  );
}

export default InventoryReceivingReport;
